import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { RHFAutocomplete, RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { InputAdornment, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

export default function EmpExpNewEditForm({ desgData, cntryData, filterCurrency, trigger, switchState, showButton, onEmptyExperience }) {
  const { control, setValue, watch, resetField, getValues, clearErrors } = useFormContext();

  const subscr = JSON.parse(localStorage.getItem('selectedSubscrSite'));

  // Set default currency
  const cur = subscr.country;


  const watchedCandExp = watch('empExp');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'empExp',
  });

  const handleAdd = () => {
    append({
      expSrno: 0,
      compNm: '',
      designation: null,
      ctc: 0,
      ctccurncy: cur || null,
      country: null,
      currentywrkng: '',
      expfromDate: null,
      exptoDate: null,
    });
  };

  const handleRemove = (index) => {
    remove(index);
  };

  // Only call onEmptyExperience() after mount and update, not on initial render to prevent inconsistency
  const hasMounted = useRef(false);

  useEffect(() => {
    if (hasMounted.current && fields.length === 0) {
      onEmptyExperience?.();
    }
    hasMounted.current = true;
  }, [fields.length, onEmptyExperience]);


  return (
    <>
      <Box
        mt={4}
        rowGap={3}
        columnGap={2}
        display="grid"
        gridTemplateColumns={{
          xs: 'repeat(1, 1fr)',
          sm: 'repeat(2, 1fr)',
        }}
        mb={3}
      >
        <Box>
          <Typography variant="h6" sx={{ color: 'text.disabled', mb: 3 }}>
            Experience Details:
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-end', md: 'center' }}
          justifyContent="flex-end"
        >
          <Button
            size="small"
            color="primary"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={handleAdd}
            sx={{ flexShrink: 0, minWidth: '140px', justifyContent: 'flex-start' }}
          >
            Add Experience
          </Button>
        </Stack>
      </Box>
      <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />} spacing={2}>
        {fields.map((item, index) => (
          <Stack key={item.id} spacing={1.5}
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: item.fromCandidate ? 'grey.100' : 'transparent',
              border: item.fromCandidate ? '0.5px dashed grey' : 'none',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: 'primary', mb: 1 }}>
              Experience {index + 1}:
            </Typography>
            <Box
              rowGap={1.5}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'none' } }}>
                <RHFTextField name={`empExp[${index}].expSrno`} />
              </Box>
              <RHFTextField
                name={`empExp[${index}].compNm`}
                label={
                  <div>
                    Employer Name<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                  </div>
                }
              />

              <Stack spacing={1.5}>
                <RHFAutocomplete
                  name={`empExp[${index}].designation`}
                  placeholder="Select Designation"
                  label={
                    <div>
                      Designation<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  options={desgData}
                  showPlusIcon
                  getOptionLabel={(option) => option.masterName}

                  renderOption={(props, option) => (
                    <li {...props} key={option.masterId}>
                      {option.masterName}
                    </li>
                  )}
                />
              </Stack>
              <Stack spacing={1.5}>
                <RHFAutocomplete
                  name={`empExp[${index}].country`}
                  placeholder="Employer Country"
                  label="Employer Country"
                  options={cntryData}
                  onChange={(event, value) => {
                    setValue(`empExp[${index}].country`, value, { shouldValidate: true });
                    setValue(`empExp[${index}].ctccurncy`, value)
                  }}
                  getOptionLabel={(option) => option.countryName}
                  renderOption={(props, option) => (
                    <li {...props} key={option.countriesId}>
                      {option.countryName}
                    </li>
                  )}
                  sx={{ mb: 1 }}
                />
              </Stack>
              <RHFTextField
                name={`empExp[${index}].ctc`}
                label="CTC"
                placeholder="0"
                type="number"
                onWheel={(e) => e.target.blur()}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        <RHFAutocomplete
                          name={`empExp[${index}].ctccurncy`}
                          filterOptions={filterCurrency}
                          sx={{
                            '& fieldset': { border: 'none' },
                          }}
                          options={cntryData}
                          disableClearable  // used to remove the cross icon
                          getOptionLabel={(option) => option?.currencySymbol || ''}
                          isOptionEqualToValue={(option, value) =>
                            option?.countriesId === value?.countriesId
                          } onChange={(event, value) => {
                            setValue(`empExp[${index}].ctccurncy`, value, { shouldValidate: true });
                            trigger(`empExp[${index}].ctc`)
                          }}
                          componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                          renderOption={(props, option) => (
                            <li {...props} key={option.shortName}>
                              {option.countryName}({option.currencySymbol})
                            </li>
                          )}
                        />
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
               <RHFCheckbox
                name={`empExp[${index}].currentywrkng`}
                label="Currently Working"
                labelPlacement="start"
                onChange={(e) => {
                  const isChecked = e.target.checked;

                  // Update the field value
                  setValue(`empExp[${index}].currentywrkng`, isChecked);

                  trigger(`empExp[${index}].exptoDate`);
                  trigger(`empExp[${index}].expfromDate`);
                }}
                sx={{
                  '&.MuiFormControlLabel-labelPlacementStart': {
                    margin: 0,
                  },
                }}
              />
            </Box>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <Controller
                name={`empExp[${index}].expfromDate`}
                control={control}
                rules={{
                  validate: (fromDateValue, formValues) => {
                    if (!fromDateValue) {
                      return 'From date is required';
                    }
                    const toDateValue = formValues?.empExp?.[index]?.exptoDate ?? getValues(`empExp[${index}].exptoDate`);
                    if (!toDateValue) return true;

                    const parseDateSafe = (val) => {
                      if (!val) return null;
                      if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : new Date(val);
                      if (typeof val === 'string') {
                        let d = new Date(val);
                        if (!Number.isNaN(d.getTime())) return d;
                        const parts = val.split(/[-/]/);
                        if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
                           d = new Date(parts[2], parts[1] - 1, parts[0]);
                           if (!Number.isNaN(d.getTime())) return d;
                        }
                      }
                      if (val && typeof val.toDate === 'function') {
                         const d = val.toDate();
                         if (!Number.isNaN(d.getTime())) return d;
                      }
                      return null;
                    };

                    const fromDateNorm = parseDateSafe(fromDateValue);
                    const toDateNorm = parseDateSafe(toDateValue);

                    if (!fromDateNorm) return 'Invalid From Date';
                    if (!toDateNorm) return true;

                    fromDateNorm.setHours(0, 0, 0, 0);
                    toDateNorm.setHours(0, 0, 0, 0);

                    if (fromDateNorm >= toDateNorm) {
                      return 'From date must be less than To date';
                    }

                    return true;
                  }
                }}
                render={({ field, fieldState: { error } }) => {
                  let value = field.value;
                  if (value) {
                    const parsedValue = new Date(value);
                    if (!Number.isNaN(parsedValue.getTime())) {
                      value = parsedValue;
                    } else {
                      value = null;
                    }
                  } else {
                    value = null;
                  }

                  return (
                    <DatePicker
                      label={
                        <div>
                          From Date<span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                        </div>
                      }
                      value={value}
                       onChange={(newValue) => {
                        field.onChange(newValue);
                        setTimeout(() => {
                          trigger(`empExp[${index}].expfromDate`); // Trigger validation for "From Date"
                          trigger(`empExp[${index}].exptoDate`); // Trigger validation for "To Date"
                          // NOTE: intentionally not calling trigger('empExp') here.
                          // That validates the WHOLE empExp array (every entry,
                          // every field — including empty required compNm/
                          // designation on this or other rows) and floods
                          // unrelated fields with "required" errors the moment
                          // any date or checkbox is touched.
                        }, 0);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                          onKeyUp: () => {
                            trigger(`empExp[${index}].expfromDate`);
                            trigger(`empExp[${index}].exptoDate`);
                          },
                        },
                      }}
                      maxDate={new Date()}
                    />
                  );
                }}
              />
              <Controller
                name={`empExp[${index}].exptoDate`}
                control={control}
                rules={{
                  validate: (toDateValue, formValues) => {
                    const isCurrentlyWorking = formValues?.empExp?.[index]?.currentywrkng ?? getValues(`empExp[${index}].currentywrkng`);
                    
                    if (!isCurrentlyWorking && !toDateValue) {
                      return 'To date is required';
                    }
                    if (!toDateValue) {
                      return true;
                    }

                    const fromDateValue = formValues?.empExp?.[index]?.expfromDate ?? getValues(`empExp[${index}].expfromDate`);

                    const parseDateSafe = (val) => {
                      if (!val) return null;
                      if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : new Date(val);
                      if (typeof val === 'string') {
                        let d = new Date(val);
                        if (!Number.isNaN(d.getTime())) return d;
                        const parts = val.split(/[-/]/);
                        if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) {
                           d = new Date(parts[2], parts[1] - 1, parts[0]);
                           if (!Number.isNaN(d.getTime())) return d;
                        }
                      }
                      if (val && typeof val.toDate === 'function') {
                         const d = val.toDate();
                         if (!Number.isNaN(d.getTime())) return d;
                      }
                      return null;
                    };

                    const toDateNorm = parseDateSafe(toDateValue);
                    const fromDateNorm = parseDateSafe(fromDateValue);

                    if (!toDateNorm) return 'Invalid To Date';

                    if (fromDateNorm) {
                      toDateNorm.setHours(0, 0, 0, 0);
                      fromDateNorm.setHours(0, 0, 0, 0);
                      if (toDateNorm <= fromDateNorm) {
                        return 'To date must be greater than From date';
                      }

                      const oneMonthLater = new Date(fromDateNorm);
                      oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
                      if (toDateNorm < oneMonthLater) {
                        return 'To Date should be at least 1 month after From Date';
                      }
                    }

                    if (isCurrentlyWorking) {
                      // To Date should be maximum = currentDate + 3 months
                      const maxAllowedDate = new Date();
                      maxAllowedDate.setMonth(maxAllowedDate.getMonth() + 3);
                      maxAllowedDate.setHours(23, 59, 59, 999);
                      
                      toDateNorm.setHours(0, 0, 0, 0);
                      if (toDateNorm > maxAllowedDate) {
                        return 'To date cannot be more than 3 months in the future';
                      }
                    } else {
                      // If not currently working, toDate should be maximum today date
                      const today = new Date();
                      today.setHours(23, 59, 59, 999);
                      
                      toDateNorm.setHours(0, 0, 0, 0);
                      if (toDateNorm > today) {
                        return 'To date cannot be in the future';
                      }
                    }

                    return true;
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  const isCurrentlyWorking = watch(`empExp[${index}].currentywrkng`);

                  // Safely parse the value to a valid date or null
                  let value = field.value;
                  if (value) {
                    const parsedValue = new Date(value);
                    if (!Number.isNaN(parsedValue.getTime())) {
                      value = parsedValue;
                    } else {
                      value = null;
                    }
                  } else {
                    value = null;
                  }

                  const toDateMax = isCurrentlyWorking
                    ? new Date(new Date().setMonth(new Date().getMonth() + 3))
                    : new Date();

                  return (
                    <DatePicker
                      label={
                        <div>
                          To Date
                          {!isCurrentlyWorking && (
                            <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                          )}
                        </div>
                      }
                      value={value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                        setTimeout(() => {
                          trigger(`empExp[${index}].expfromDate`);
                          trigger(`empExp[${index}].exptoDate`);
                          // See note above: not calling trigger('empExp') —
                          // it re-validates every field in every row.
                        }, 0);
                      }}
                      maxDate={toDateMax}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: isCurrentlyWorking ? false : !!error,
                          helperText: isCurrentlyWorking ? '' : error?.message,
                          placeholder: '', // To prevent "DD/MM/YYYY" placeholder
                          onKeyUp: () => {
                            trigger(`empExp[${index}].expfromDate`);
                            trigger(`empExp[${index}].exptoDate`);
                          },
                        },
                      }}
                    />
                  );
                }}
              />
            </Box>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-end', md: 'center' }}
              justifyContent="flex-end"
            >
              <Button
                size="small"
                color="error"
                startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                onClick={() => handleRemove(index)}
              >
                Remove
              </Button>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </>
  );
}
EmpExpNewEditForm.propTypes = {
  desgData: PropTypes.array,
  cntryData: PropTypes.array,
  filterCurrency: PropTypes.func,
  trigger: PropTypes.func,
  switchState: PropTypes.object,
  showButton: PropTypes.bool,
  onEmptyExperience: PropTypes.func
};
